// Generic table-export utility — Excel + PDF in one place so every page can
// add export buttons without re-inventing the wheel.
//
// Usage:
//   import { exportToExcel, exportToPDF } from '../lib/exportTable';
//   exportToExcel(rows, { filename, sheetName, columns });
//   exportToPDF(rows,   { filename, title, columns, orientation });
//
// `columns` is an array of:
//   { key: 'asset_name', label: 'Asset', width: 30, format: (v, row) => '...' }
// If `columns` is omitted, every key from the first row is used.

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const today = () => new Date().toISOString().slice(0, 10);

const inferColumns = (rows) => {
  if (!rows || rows.length === 0) return [];
  return Object.keys(rows[0]).map(k => ({ key: k, label: k }));
};

const toCellValue = (col, row) => {
  let v = row[col.key];
  if (col.format) v = col.format(v, row);
  if (v == null) return '';
  if (v instanceof Date) return v.toLocaleString();
  if (typeof v === 'object') return JSON.stringify(v);
  return v;
};

// ──────────────────────────────────────────────────────────────────────────────
// EXCEL — uses SheetJS Community (xlsx) → file-saver
// ──────────────────────────────────────────────────────────────────────────────
export function exportToExcel(rows, opts = {}) {
  const {
    filename  = `export-${today()}.xlsx`,
    sheetName = 'Data',
    columns   = inferColumns(rows),
    title,                 // optional: prepend a title row + a blank row
    summary,               // optional: array of [label, value] meta rows
  } = opts;

  if (!rows || rows.length === 0) {
    // Still emit a workbook so the user gets visual feedback
    rows = [];
  }

  const headerRow = columns.map(c => c.label);
  const dataRows  = rows.map(r => columns.map(c => toCellValue(c, r)));

  const aoa = [];
  if (title) {
    aoa.push([title]);
    aoa.push([`Generated ${new Date().toLocaleString()}`]);
    aoa.push([]);
  }
  if (summary && summary.length) {
    for (const [k, v] of summary) aoa.push([k, v]);
    aoa.push([]);
  }
  aoa.push(headerRow);
  aoa.push(...dataRows);

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  // Auto-size columns based on header length + first 50 rows
  const sample = rows.slice(0, 50);
  ws['!cols'] = columns.map(c => {
    const max = Math.max(
      String(c.label).length,
      ...sample.map(r => String(toCellValue(c, r)).length)
    );
    return { wch: Math.min(60, Math.max(8, max + 2)) };
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), filename);
}

// ──────────────────────────────────────────────────────────────────────────────
// PDF — jsPDF + autoTable
// ──────────────────────────────────────────────────────────────────────────────
export function exportToPDF(rows, opts = {}) {
  const {
    filename    = `export-${today()}.pdf`,
    title       = 'Tag My Assets',
    subtitle,
    orientation = 'portrait',     // 'portrait' | 'landscape'
    columns     = inferColumns(rows),
    summary,                      // [[label, value], …]
    accent      = [37, 99, 235],  // matches new --accent (Tailwind blue-600)
  } = opts;

  const doc = new jsPDF({ orientation, unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();

  // Header — coloured strip
  doc.setFillColor(...accent);
  doc.rect(0, 0, pageW, 36, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 24, 23);
  if (subtitle) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(subtitle, 24, 33);
  }

  // Meta line under header
  doc.setTextColor(100, 116, 139);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated ${new Date().toLocaleString()}  ·  ${rows.length} rows`, 24, 52);

  let cursorY = 62;
  if (summary && summary.length) {
    doc.setFontSize(9);
    doc.setTextColor(15, 23, 42);
    for (const [k, v] of summary) {
      doc.text(`${k}: `, 24, cursorY);
      doc.setFont('helvetica', 'bold');
      doc.text(String(v), 90, cursorY);
      doc.setFont('helvetica', 'normal');
      cursorY += 12;
    }
    cursorY += 4;
  }

  autoTable(doc, {
    startY: cursorY,
    head: [columns.map(c => c.label)],
    body: rows.map(r => columns.map(c => toCellValue(c, r))),
    headStyles: { fillColor: accent, textColor: 255, fontSize: 9 },
    bodyStyles: { fontSize: 8, textColor: [15, 23, 42] },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: 24, right: 24 },
    didDrawPage: (data) => {
      // Footer with page number
      const str = `Page ${doc.internal.getNumberOfPages()}`;
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(str, pageW - 24, doc.internal.pageSize.getHeight() - 12, { align: 'right' });
    },
  });

  doc.save(filename);
}

// ──────────────────────────────────────────────────────────────────────────────
// Convenience: single-call export with both buttons.
// Returns a small <ExportButtons /> render-helper data structure (not JSX —
// the caller wires the button itself, so this stays pure).
// ──────────────────────────────────────────────────────────────────────────────
export function makeExporter(rows, opts = {}) {
  return {
    excel: () => exportToExcel(rows, opts),
    pdf:   () => exportToPDF(rows, opts),
  };
}
